// R-NAV-1/2 — Router role gate (spec §2.6, §2.7-2, §22.6).
//
// The SECOND protection layer on top of route-classification.ts's
// PUBLIC / AUTH_FLOW / WORKSPACE kinds. After AppShell's auth gate
// confirms a session exists, this map decides whether the session's
// ACTIVE role may see a given workspace route. An unauthorised role gets
// a denial page (NOT a redirect to /login — the user IS logged in, just
// on the wrong surface). The authoritative protection remains the api's
// 403 (@Roles + @CheckPolicies); this is defence-in-depth + better UX.
//
// PATH-AWARE (R-NAV-2): keys are either the first path segment
// ("admin" → the whole /admin/* subtree) OR a two-segment path
// ("admin/departments" → just that page). The two-segment key wins over
// the one-segment subtree default, which lets `faculty_admin` reach the
// scoped academic pages under /admin/* (departments, programs, offerings,
// enrollments) WITHOUT also unlocking the system-only ones (/admin/users,
// /admin/roles). Same mechanism lets instructor/content_manager reach
// /admin/course-content even though it lives under /admin/*.
//
// Routes NOT listed here are allowed for ANY authenticated user — the
// shared learning/account surfaces. `/audit` is intentionally absent: the
// Audit page renders its own CASL denial (read:AuditLog), finer-grained
// than a static role list.

import type { RoleId } from "../role";

/** routeKey (1- or 2-segment) → RoleIds permitted. 2-seg overrides 1-seg. */
export const ROUTE_ROLES: Readonly<Record<string, readonly RoleId[]>> = {
  // ----- one-segment subtree defaults -----
  // System administration — the whole /admin/* subtree defaults to system
  // admins; faculty-scoped pages are re-opened via 2-segment keys below.
  admin: ["admin", "super_admin"],
  super: ["super_admin"],
  // R-MON W12/W13 — system-health console (پایش سامانه). Mirrors the backend
  // @Roles("admin","super_admin") on GET /v1/admin/system-health: defence-in-depth
  // + a clean role-denial for everyone else (the page was previously unkeyed = open
  // to any authed user, the API @Roles scoped).
  monitoring: ["admin", "super_admin"],
  parent: ["parent"],
  instructor: ["instructor", "admin", "super_admin"],
  authoring: ["instructor", "content_manager", "admin", "super_admin"],
  // R-MARKET P1 — «بازار دانش» authoring studio (/market/studio + /market/studio/:id).
  // Course AUTHORS only (doc §1.2: استاد سازنده + مدیر بازار): instructor /
  // content_manager + admin/super_admin oversight. The 1-seg "market" key covers
  // the whole /market/* subtree. The API @Roles-guards every call (defence-in-
  // depth); the PUBLIC marketplace browse tab is P4 (not gated here yet).
  // S-NRI-ADAPT — «سازندهٔ پکیج» (creator) نیز نویسندهٔ بازار است ⇒ /market/studio
  // (زیرمجموعهٔ subtreeِ market) را می‌بیند؛ review/earnings/admin با کلید ۲-قطعه‌ای
  // جدا می‌مانند و creator را شامل نمی‌شوند.
  market: ["instructor", "content_manager", "creator", "admin", "super_admin"],
  // R-MARKET P3 — the REVIEW surface (/market/review + /market/review/:id) is the
  // REVIEWER's, not the author's: content_manager (doc §1.2 locked decision) + admin/
  // super_admin oversight. This 2-segment key WINS over the "market" subtree key above
  // (which would otherwise let an instructor author reach the review queue). The API
  // @Roles-guards every review route (defence-in-depth); a wrong role gets the clean
  // role-denial page.
  "market/review": ["content_manager", "admin", "super_admin"],
  // R-MARKET OPEN-RESPONSE (Gap 9) — the author STAFF grading queue (/market/grading-queue/:id).
  // Course AUTHORS + oversight, identical to the author "market" subtree (instructor /
  // content_manager + admin/super_admin). The 2-seg key keeps it explicit (and would let us
  // narrow it later); the API @Roles-guards every route and 404s a cross-author course.
  "market/grading-queue": ["instructor", "content_manager", "admin", "super_admin"],
  // R-MARKET P5 — economics surfaces under /market/* (each 2-seg key WINS over the
  // author-only "market" subtree key above).
  //   «دوره‌های من» (buyer shelf) — ANY authenticated user may have purchases, so
  //   every role is allowed (the 1-seg "market" key would otherwise wrongly block a
  //   student/parent buyer). The backend my-courses endpoint carries no @Roles.
  "market/my-courses": [
    "student",
    "creator",
    "instructor",
    "admin",
    "faculty_admin",
    "parent",
    "org",
    "ta",
    "content_manager",
    "support",
    "moderator",
    "super_admin",
    "finance_manager",
    "counselor",
    // S-NRI-3 — the buyer shelf is universal (any authed role may hold purchases);
    // the market group is injected into every role's sidebar, so relist the new roles.
    "holding_manager",
    "company_admin",
    "company_training_manager",
    "certification_officer",
    "practical_assessor",
    "ministry_supervisor",
    "auditor",
  ],
  //   R-MARKET P6 — «یادگیری دوره» (the buyer's course player, /market/learn/:id).
  //   ANY authenticated user may have a purchase (same premise as «دوره‌های من»), so
  //   every role is allowed here — the 1-seg "market" author key would otherwise wrongly
  //   block a student/parent buyer. The backend player endpoint carries no @Roles and
  //   404s a non-enrolled caller (the real gate). 2-seg key WINS over "market".
  "market/learn": [
    "student",
    "creator",
    "instructor",
    "admin",
    "faculty_admin",
    "parent",
    "org",
    "ta",
    "content_manager",
    "support",
    "moderator",
    "super_admin",
    "finance_manager",
    "counselor",
    // S-NRI-3 — same buyer premise as «دوره‌های من»: any authed role may enroll.
    "holding_manager",
    "company_admin",
    "company_training_manager",
    "certification_officer",
    "practical_assessor",
    "ministry_supervisor",
    "auditor",
  ],
  //   R-MARKET EXT — «علاقه‌مندی‌های من» (the buyer's wishlist, /market/wishlist). ANY
  //   authenticated user may save a course (same premise as «دوره‌های من»), so every role
  //   is allowed; the backend wishlist endpoints carry no @Roles and scope to the caller.
  //   2-seg key WINS over the author-only "market" subtree key.
  "market/wishlist": [
    "student",
    "creator",
    "instructor",
    "admin",
    "faculty_admin",
    "parent",
    "org",
    "ta",
    "content_manager",
    "support",
    "moderator",
    "super_admin",
    "finance_manager",
    "counselor",
    // S-NRI-3 — same buyer premise as «دوره‌های من»: any authed role may save a course.
    "holding_manager",
    "company_admin",
    "company_training_manager",
    "certification_officer",
    "practical_assessor",
    "ministry_supervisor",
    "auditor",
  ],
  //   «درآمد من» (author earnings) — the course AUTHORS (doc §1.2): instructor /
  //   content_manager + admin/super_admin oversight. Mirrors the backend @Roles.
  "market/earnings": ["instructor", "content_manager", "admin", "super_admin"],
  //   «مدیریت بازار» (economics config + sales report) — admin/super_admin only.
  //   Mirrors the backend @Roles on /v1/market/admin/*.
  "market/admin": ["admin", "super_admin"],
  gradebook: ["instructor", "ta", "admin", "super_admin"],
  content: ["content_manager", "admin", "super_admin"],
  ta: ["ta", "admin", "super_admin"],
  // R-MON W7 — the TA's scoped academic surfaces. These are the SAME read/draft
  // pages the instructor uses; the backend now admits a section-assistant on its
  // ASSIGNED sections (course-ownership + sectionForActor assistants branch) and
  // 404s elsewhere. The pages were previously UNKEYED (any authed user reached
  // them, the API scoped). Keying them to the teaching roles is defence-in-depth +
  // a clean role-denial for everyone else. NOTE: «ثبت نمرات» (grade-submission)
  // admits a TA for DRAFT entry, but the FINALIZE (قطعی) action is instructor/
  // admin-only — enforced server-side (403) AND hidden in the UI for a TA.
  roster: ["instructor", "ta", "faculty_admin", "admin", "super_admin"],
  "class-analytics": ["instructor", "ta", "faculty_admin", "admin", "super_admin"],
  "item-analysis": ["instructor", "ta", "faculty_admin", "admin", "super_admin"],
  "grade-submission": ["instructor", "ta", "faculty_admin", "admin", "super_admin"],
  // S-NRI-5B.1 — «گزارش‌ها و تحلیل‌ها» (AdminAnalytics) دیگر صفحهٔ معدل/دانشجو نیست؛
  // روی GET /v1/competency/org-rollup سوار است. این کلید عمداً همان
  // MANAGER_READ_ROLES بک‌اند (org-rollup.controller.ts) را آینه می‌کند تا FE
  // laxer از API نباشد. انزوا همچنان سمت سرور است (زیردرختِ خودِ کاربر؛ بیرون = ۴۰۴)؛
  // این لایه فقط دفاع-در-عمق و ردِ تمیزِ نقش است.
  "analytics-admin": [
    "admin",
    "super_admin",
    "ministry_supervisor",
    "auditor",
    "holding_manager",
    "company_admin",
    "company_training_manager",
    "certification_officer",
    "practical_assessor",
  ],
  support: ["support", "admin", "super_admin"],
  moderate: ["moderator", "admin", "super_admin"],
  // R-MON W10 — the moderator's work pages were UNKEYED (any authed user reached
  // them; the API @Roles scoped). Key them to the oversight roles: defence-in-depth
  // + a clean role-denial for everyone else. Mirrors the backend @Roles on
  // /v1/moderation/* and the community moderation/stats routes.
  "flagged-reports": ["moderator", "admin", "super_admin"],
  "community-moderation": ["moderator", "admin", "super_admin"],
  "auto-rules": ["moderator", "admin", "super_admin"],
  "moderation-log": ["moderator", "admin", "super_admin"],
  org: ["org", "admin", "super_admin"],
  // faculty_admin home + its dedicated (placeholder) tools.
  "faculty-dashboard": ["faculty_admin", "admin", "super_admin"],
  "curriculum-builder": ["faculty_admin", "admin", "super_admin"],
  "instructor-assignment": ["faculty_admin", "admin", "super_admin"],
  "probation-review": ["faculty_admin", "admin", "super_admin"],
  // "special-cases" gate dropped in S-21 with its stale placeholder route —
  // the real page is /admin/commission (gated under the admin subtree).
  "faculty-reports": ["faculty_admin", "admin", "super_admin"],
  // "service-desk-admin" gate dropped in S-21 — the route never existed (the
  // staff approval inbox SHIPPED as a tab inside /service-desk; sidebars point
  // there). A gate key on a dead route now fails the nav-coherence vitest.

  // WELL-4 — «مشاور دانشگاه» (counselor) workspace. LEAST-PRIVILEGE: the counselor
  // reaches only its own inbox + appointment queue + the tenant's open wellness
  // crisis queue (duty-of-care). admin/super_admin permitted for support/QA. The
  // api enforces the real scoping (own-appointments / open-alert-only summary,
  // 404 on anything else); this gate is defence-in-depth + a clean denial for
  // every other role.
  "counselor-inbox": ["counselor", "admin", "super_admin"],
  "counseling-appointments": ["counselor", "admin", "super_admin"],
  "crisis-queue": ["counselor", "admin", "super_admin"],
  // R-MON W9 — counselor caseload / monitoring (کاربرگ مراجعان). Same audience as
  // the rest of the desk: the api scopes every read to the caller's OWN caseload +
  // LINKED students (aggregate roll-up has no per-student rows; timeline 404s on an
  // unlinked student). This gate is defence-in-depth + a clean denial for everyone else.
  "counselor-caseload": ["counselor", "admin", "super_admin"],

  // ----- two-segment overrides under /admin/* -----
  // S-NRI-3 — the ORGANIZATIONAL tree (وزارت→هلدینگ→شرکت→واحد) at /admin/organizations
  // is READ-only oversight for the new org/oversight roles. The 2-seg key WINS over
  // the admin-only /admin/* subtree default, so admin+super_admin MUST be relisted
  // here or they'd lose the page. Exactly the five roles whose nav points here are
  // added — the write-admin pages (/admin/users, /admin/roles) stay admin-only.
  "admin/organizations": [
    "admin",
    "super_admin",
    "holding_manager",
    "company_admin",
    "company_training_manager",
    "ministry_supervisor",
    "auditor",
  ],
  // S-NRI-3.5 — each org/oversight role's dedicated dashboard home. Gated to the
  // OWNING role + admin/super_admin (who may view any workspace). Defence-in-depth:
  // the underlying org data endpoints are independently org-scoped server-side.
  "ministry-dashboard": ["ministry_supervisor", "admin", "super_admin"],
  "holding-dashboard": ["holding_manager", "admin", "super_admin"],
  "company-dashboard": ["company_admin", "admin", "super_admin"],
  "training-dashboard": ["company_training_manager", "admin", "super_admin"],
  "audit-dashboard": ["auditor", "admin", "super_admin"],
  "certification-desk": ["certification_officer", "admin", "super_admin"],
  "assessor-dashboard": ["practical_assessor", "admin", "super_admin"],
  // S-NRI-ADAPT — «سازندهٔ پکیج» studio home (placeholder «استودیو دانش»). Real
  // studio/payout land with S-NRI-6. Gated to creator + admin/super_admin oversight.
  "creator-studio": ["creator", "admin", "super_admin"],
  // faculty_admin runs academic ops for THEIR faculty (api 403 enforces the
  // facultyId scope; this just lets them reach the page).
  "admin/departments": ["admin", "super_admin", "faculty_admin"],
  "admin/programs": ["admin", "super_admin", "faculty_admin"],
  "admin/offerings": ["admin", "super_admin", "faculty_admin"],
  "admin/courses": ["admin", "super_admin", "faculty_admin"],
  // R-COMPLY W2 — گرایش‌ها + کمیسیون موارد خاص: the backends (specializations,
  // commission) already grant + faculty-scope faculty_admin (§8.10); the gate
  // omitted them, so the dean's own sidebar links dead-ended. Open them.
  "admin/specializations": ["admin", "super_admin", "faculty_admin"],
  "admin/commission": ["admin", "super_admin", "faculty_admin"],
  // مدیریت ثبت‌نام (/admin/enrollments) is admin-only at the API (manual enroll /
  // transition = registrar/admissions, not a faculty dean). faculty_admin removed
  // here (was a reverse dead-end: gate allowed the page, API 403'd) — the dean
  // uses /admin/student-overview (faculty-scoped) for their students instead.
  "admin/enrollments": ["admin", "super_admin"],
  // R-MON W6 «پروندهٔ افراد» — role-aware person-360 (student/instructor).
  // faculty_admin sees only their faculty's people (api 403/404 enforces the
  // facultyId scope: student via program→dept→faculty, instructor via
  // instructor→dept→faculty); this gate just reaches the page.
  "admin/student-overview": ["admin", "super_admin", "faculty_admin"],
  // R-MED — stage-based progress (پزشکی/دندان/دارو). The dean (faculty_admin)
  // READS their faculty's stage path + structure (stage-progress + program-stages
  // backends both grant + faculty-scope faculty_admin); the recording/CRUD
  // mutations are admin-guarded server-side. The page reflects read-only for the
  // dean. Without this 2-seg override the /admin/* subtree default (admin-only)
  // dead-ended the dean's new sidebar link.
  "admin/stage-progress": ["admin", "super_admin", "faculty_admin"],
  // Course content authoring is instructor/content territory, even though the
  // page lives under /admin/*.
  "admin/course-content": ["admin", "super_admin", "instructor", "content_manager"],
  // R-MON W4 — parent↔student link onboarding. The backend grants admin/
  // super_admin/support (@Roles + @AuditAction); support runs family onboarding
  // as part of help-desk duties. The /admin/* subtree default is admin-only, so
  // without this 2-seg override support's link to the page would dead-end (and
  // the page is otherwise admin-shaped). The api enforces the same role set.
  "admin/parent-links": ["admin", "super_admin", "support"],

  // R-COMPLY — finance (شهریه/خزانه/حقوق/احکام/وام/بورسیه) is مدیر مالی's job,
  // NOT ریاست دانشگاه (admin). These 2-seg keys grant finance_manager (+ super_admin
  // break-glass) and EXCLUDE admin; the api @Roles enforce the same. "هر نقش وظیفهٔ خودش".
  "admin/finance": ["finance_manager", "super_admin"],
  "admin/tuition-config": ["finance_manager", "super_admin"],

  // S-18 — HR / hiring (استخدام). Four audiences: admin/faculty_admin run the
  // application + contract lifecycle; finance_manager assigns the salary decree
  // (the API enforces the finer split — finance can't review apps, admin can't
  // assign salary). faculty_admin is faculty-scoped server-side.
  "admin/hr": ["admin", "faculty_admin", "finance_manager", "super_admin"],

  // S-4/5 موج ۲ — tenant-wide آیین‌نامهٔ آموزشی. Explicit (the `admin` subtree
  // default already grants these two; the key is self-documenting + endpoint-matched).
  "admin/regulation": ["admin", "super_admin"],

  // R-MON W5 — the welfare-fund / bursary / refund pages are DUAL-AUDIENCE: a
  // student APPLIES (the page renders the apply view; the apply endpoints carry no
  // @Roles, the service requires a Student record) and مدیر مالی REVIEWS (the page
  // renders the review queue; the approve/reject/disburse/refund endpoints are
  // @Roles("finance_manager","super_admin")). Without a key these routes were
  // unkeyed = open to ANY authed user. Gate them to exactly the two audiences:
  // student (apply) + finance_manager/super_admin (review). admin is EXCLUDED
  // (R-COMPLY: not a finance owner) and gets the clean role-denial page.
  // tuition/loans + tuition/refund are 2-seg keys so the bare /tuition (student
  // balance/pay) stays open to any authed user (it has no key).
  "tuition/loans": ["student", "finance_manager", "super_admin"],
  "tuition/refund": ["student", "finance_manager", "super_admin"],
  "financial-aid": ["student", "finance_manager", "super_admin"],

  // R-PAY-2d — financial clearance (تسویه‌حساب) is DUAL-AUDIENCE like refund: a
  // student sees their OWN clearance; مدیر مالی / ریاست‌دانشکده (faculty_admin) /
  // admin view it for a selected student (bursar). Mirrors the backend
  // @Roles("finance_manager","super_admin","admin","faculty_admin") on
  // GET /v1/finance/clearance/:studentId; student own-clearance carries no @Roles.
  "tuition/clearance": ["student", "finance_manager", "super_admin", "admin", "faculty_admin"],

  // WELL-1 — wellness tests (غربالگری/خودشناسی). Student-primary; admin/
  // super_admin permitted for support/QA, mirroring the wellness backend's
  // @Roles("student","admin","super_admin"). The api self-scopes every attempt/
  // result read+write (404 on anyone else's); this gate is defence-in-depth +
  // a clean denial for non-student roles. /wellness itself is left ungated (any
  // authenticated user reaches the dashboard + the always-visible safety floor).
  "wellness/tests": ["student", "admin", "super_admin"],
  "wellness/test": ["student", "admin", "super_admin"],
  "wellness/result": ["student", "admin", "super_admin"],
  "wellness/results": ["student", "admin", "super_admin"],
};

/**
 * True if the active role may view the route. `pathKey` is the first two
 * path segments joined (e.g. "admin/departments"); the 2-segment match wins,
 * then the 1-segment `routeId` subtree default, else any authenticated user.
 * A null activeRoleId is never allowed on a gated route (AppShell's auth gate
 * handles the no-session case first).
 */
export const isRouteAllowedForRole = (
  routeId: string,
  pathKey: string,
  activeRoleId: RoleId | null,
): boolean => {
  const allowed = ROUTE_ROLES[pathKey] ?? ROUTE_ROLES[routeId];
  if (!allowed) return true; // not role-specific → any authenticated user
  if (!activeRoleId) return false;
  return allowed.includes(activeRoleId);
};
